import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import HCaptcha from "@hcaptcha/react-hcaptcha";

// Routes
import paths from "routes/paths";

// Context
import { UserContext } from "contexts/UserContext";

// Utils
import { checkEmailInput, checkPasswordInput } from "utils/validations";
import { signInUser } from "utils/api/user/requests";
import { analytics } from "utils/analytics";

// Components
import { ForgotPasswordModal } from "pages/Login/ForgotPasswordModal";
import { SocialButton } from "pages/Login/SocialButton";
import { Divider, Flex } from "components/spocketUI/";
import { default as Loadable } from "components/_Shared/commonLoadable";

// Actions
// TODO these auth steps are tied to Redux. We will remove them in the future
import {
  setupCredentialStorage,
  updateSettingsFromCredentials,
  signIn,
  register,
  verifyAuth
} from "utils/actions";

// Assets
import spocketLogo from "assets/images/spocket-logo.svg";

// Style
import "../../index.css"; // TODO We need to change our base CSS to remove bloat and use Styled-Components
import {
  Page,
  Container,
  Logo,
  Form,
  StyledInput,
  StyledButton,
  ErrorText,
  ForgotPasswordButton,
  SocialButtonsContainer,
  SignupLinkContainer,
  LinkText,
  StyledLink,
  AuthErrorText
} from "./Login.style";

const LoginComponent = ({ signIn, register, verifyAuth }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailUsedOnSignup } = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [authError, setAuthError] = useState("");
  const [forgotPasswordModalIsOpen, setForgotPasswordModalIsOpen] = useState(false);
  const [validatedToken, setValidatedToken] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const captchaRef = useRef(null);
  const hCaptchaKey = process.env.REACT_APP_HCAPTCHA_SITE_KEY;
  const [captchaToken, setCaptchaToken] = useState(null);

  useEffect(() => {
    document.title = t("Login.PageTitle");
    const hasOpenModalOnUrl = window.location.search.includes("open-forgot-password-modal");
    if (hasOpenModalOnUrl) setForgotPasswordModalIsOpen(true);
  }, []);

  useEffect(() => {
    if (emailUsedOnSignup) {
      setEmail(emailUsedOnSignup);
      setEmailError(t("Login.Error.EmailExists"));
    }
  }, [emailUsedOnSignup]);

  const validate = () => {
    const emailError = checkEmailInput(email);
    const passError = checkPasswordInput(password);
    setEmailError(emailError);
    setPasswordError(passError);
    return emailError.length > 0 || passError.length > 0;
  };

  useEffect(() => {
    if (captchaToken) handleSubmit();
  }, [captchaToken]);

  const checkCaptcha = async event => {
    event.preventDefault();
    try {
      const captchaResponse = await captchaRef.current.execute({
        async: true
      });
      const responseToken = captchaResponse.response;
      setCaptchaToken(responseToken);
    } catch {
      setAuthError(t("Checkout.Error.Captcha"));
    }
  };

  const handleSubmit = () => {
    if (captchaToken || validatedToken) {
      const hasErrors = validate();
      if (!hasErrors) {
        setIsLoading(true);
        signInUser({
          email,
          password,
          hCaptchaToken: captchaToken || validatedToken
        })
          .then(res => {
            const { store_credential: storeCredential } = res;
            const { storeId } = storeCredential;
            setupCredentialStorage(res);
            updateSettingsFromCredentials(res);
            signIn();
            register();
            verifyAuth();
            // Redirection logic
            const redirectDestination = localStorage.getItem("redirect_to_url_after_signin") || "/search";
            navigate(redirectDestination);
            localStorage.removeItem("redirect_to_url_after_signin");
            // Segment - Identify user when they sign up (Signup)
            analytics.identify(storeId, { source: "web" });
            // Segment - conversion (Signup)
            analytics.track("login_complete", { storeId: storeId, source: "web" });
          })
          .catch(error => {
            const { response = {} } = error;
            const { data } = response;
            if (data) {
              const { message } = data;
              setAuthError(message || t("Login.Error.Generic"));
            } else {
              setAuthError(t("Login.Error.Generic"));
            }
            setIsLoading(false);
          });
      }
    } else {
      setAuthError(t("Checkout.Error.Captcha"));
    }
  };

  return (
    <Loadable active={isLoading} spinnerSize="10px">
      <Page>
        <Container>
          <Logo src={spocketLogo} />
          {authError.length > 0 ? <AuthErrorText data-cy="login-error">{authError}</AuthErrorText> : null}
          <Form onSubmit={checkCaptcha}>
            <StyledInput
              name="email"
              type="text"
              placeholder="Email"
              data-cy="login-text"
              value={email}
              onChange={val => {
                setEmail(val.target.value);
                setEmailError("");
                setAuthError("");
              }}
            />
            {emailError ? <ErrorText>{emailError}</ErrorText> : null}
            <StyledInput
              name="password"
              type="password"
              placeholder="Password"
              data-cy="login-password"
              value={password}
              onChange={val => {
                setPassword(val.target.value);
                setPasswordError("");
                setAuthError("");
              }}
            />
            {passwordError ? <ErrorText>{passwordError}</ErrorText> : null}
            <StyledButton title={t("Login.Button.Title")} data-cy="login-button">
              {t("Login.Button.Label")}
            </StyledButton>
          </Form>
          <ForgotPasswordButton
            title={t("Login.ForgotLink.Title")}
            onClick={() => {
              setForgotPasswordModalIsOpen(true);
            }}
          >
            {t("Login.ForgotLink.Label")}
          </ForgotPasswordButton>
          <Flex marginTop="15px" marginBottom="15px">
            <HCaptcha
              sitekey={hCaptchaKey}
              onVerify={token => {
                setValidatedToken(token);
              }}
              onError={() => {
                setAuthError(t("Checkout.Error.Captcha"));
              }}
              ref={captchaRef}
              size="invisible"
            />
          </Flex>
          <Divider>{t("Login.Divider")}</Divider>
          <SocialButtonsContainer>
            <SocialButton type="google" />
            <SocialButton type="facebook" />
          </SocialButtonsContainer>
        </Container>
        <SignupLinkContainer>
          <LinkText>{t("Login.SignupLink.Intro")}</LinkText>
          <StyledLink
            to={`${paths.auth.signup}?utm_source=login_page&utm_medium=sign_up_now_button&utm_campaign=redirection_signup`}
            title={t("Login.SignupLink.Label")}
            data-cy="sign-up"
          >
            {t("Login.SignupLink.Label")}
          </StyledLink>
        </SignupLinkContainer>
        <ForgotPasswordModal open={forgotPasswordModalIsOpen} onClose={setForgotPasswordModalIsOpen} />
      </Page>
    </Loadable>
  );
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ signIn, register, verifyAuth }, dispatch);
}

function mapStateToProps(state) {
  return {};
}

export const Login = connect(mapStateToProps, mapDispatchToProps)(LoginComponent);
